/**
 * Which module a page works in: two cards, one selected. Firm configuration
 * is the one page that switches module in place; everywhere else the module
 * is chosen on a client's module page (`ClientModulesPage`).
 *
 * A radio group, not two buttons — exactly one module is always current. The
 * selected card is the one tab stop; arrow keys move to and select the other
 * (WAI-ARIA radio group pattern), Space or Enter selects the focused card.
 * Selecting writes the URL (`useModule().setModule`), so the choice survives a
 * reload and a shared link.
 */

import { useRef, type KeyboardEvent } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
	MODULE_BLURBS,
	MODULE_CODES,
	MODULE_LABELS,
	radioKeyTarget,
	type ModuleCode,
} from './modules';
import { useModule } from './useModule';

export function ModuleSelector() {
	const { module, setModule } = useModule();
	const cards = useRef<Array<HTMLElement | null>>([]);
	/* Choosing the current module again changes nothing — no history entry,
     and the selected group stays. */
	const choose = (code: ModuleCode) => {
		if (code !== module) setModule(code);
	};

	const onKeyDown = (event: KeyboardEvent, index: number) => {
		if (event.key === ' ' || event.key === 'Enter') {
			event.preventDefault();
			choose(MODULE_CODES[index]!);
			return;
		}
		const target = radioKeyTarget(index, event.key, MODULE_CODES.length);
		if (target === null) return;
		event.preventDefault();
		choose(MODULE_CODES[target]!);
		cards.current[target]?.focus();
	};

	return (
		<Box
			role="radiogroup"
			aria-label="Module"
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
				gap: 'var(--s3)',
				maxWidth: 720,
			}}
		>
			{MODULE_CODES.map((code: ModuleCode, index) => {
				const selected = code === module;
				return (
					<Box
						key={code}
						ref={(node: HTMLElement | null) => {
							cards.current[index] = node;
						}}
						role="radio"
						aria-checked={selected}
						tabIndex={selected ? 0 : -1}
						onClick={() => choose(code)}
						onKeyDown={(event) => onKeyDown(event, index)}
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: '10px',
							minWidth: 0,
							p: '10px 14px',
							bgcolor: selected
								? 'var(--accent-surface)'
								: 'var(--surface)',
							border: '1px solid',
							borderColor: selected
								? 'var(--accent)'
								: 'var(--line)',
							/* A second ring inside the border, so the selected card reads
                 as chosen without relying on the tint alone. */
							boxShadow: selected
								? 'inset 0 0 0 1px var(--accent)'
								: 'var(--e1)',
							borderRadius: '10px',
							cursor: 'pointer',
							userSelect: 'none',
							transition:
								'border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease)',
							'&:hover': selected
								? {}
								: { borderColor: 'var(--accent-border)' },
							'&:focus-visible': {
								outline: '2px solid var(--accent)',
								outlineOffset: '2px',
							},
							'@media (prefers-reduced-motion: reduce)': {
								transition: 'none',
							},
						}}
					>
						<Box
							aria-hidden="true"
							sx={{
								display: 'grid',
								placeItems: 'center',
								pt: '1px',
								color: selected
									? 'var(--accent)'
									: 'var(--ink-3)',
								'& svg': { fontSize: 18 },
							}}
						>
							{selected ? (
								<CheckCircleIcon />
							) : (
								<RadioButtonUncheckedIcon />
							)}
						</Box>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Typography
								component="span"
								sx={{
									display: 'block',
									fontSize: 'var(--fs-section)',
									fontWeight: 600,
									color: 'var(--ink)',
								}}
							>
								{MODULE_LABELS[code]}
							</Typography>
							<Typography
								component="span"
								sx={{
									display: 'block',
									fontSize: 'var(--fs-small)',
									color: 'var(--ink-3)',
								}}
							>
								{MODULE_BLURBS[code]}
							</Typography>
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}
